import type { ProofStandard } from "@/hooks/useProofStandards";

export const UNIT_CHECK_PROOF_KEY_SEP = "::";

export function unitCheckProofKey(standardId: string, roomName: string): string {
  return `${standardId}${UNIT_CHECK_PROOF_KEY_SEP}${roomName}`;
}

export function parseUnitCheckProofKey(key: string): { standardId: string; roomName: string } {
  const idx = key.indexOf(UNIT_CHECK_PROOF_KEY_SEP);
  if (idx === -1) return { standardId: key, roomName: "" };
  return {
    standardId: key.slice(0, idx),
    roomName: key.slice(idx + UNIT_CHECK_PROOF_KEY_SEP.length),
  };
}

/** Rooms where this unit check must be completed, in checklist walk order. */
export function getProofStandardRooms(
  s: ProofStandard,
  checklistRoomNames: string[],
  generalLabel = "General",
): string[] {
  const fromRooms = (s.rooms ?? []).map((r) => r.trim()).filter(Boolean);
  if (fromRooms.length > 0) {
    const order = new Map(checklistRoomNames.map((n, i) => [n.trim(), i]));
    return [...new Set(fromRooms)].sort(
      (a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999) || a.localeCompare(b),
    );
  }
  if (s.room?.trim()) return [s.room.trim()];
  return [generalLabel];
}

export function proofUrlsForUnitCheck(
  map: Record<string, string[]>,
  standardId: string,
  roomName: string,
): string[] {
  const composite = unitCheckProofKey(standardId, roomName);
  if (map[composite]?.length) return map[composite];
  const legacy = map[standardId];
  if (legacy?.length) {
    const rooms = roomName ? [roomName] : [];
    if (rooms.length === 0 || !map[composite]) return legacy;
  }
  return [];
}

function functionalPassForKey(
  passKey: string,
  standardId: string,
  functionalPassMap: Record<string, boolean> | Set<string>,
): boolean {
  if (functionalPassMap instanceof Set) {
    return functionalPassMap.has(passKey) || functionalPassMap.has(standardId);
  }
  return !!functionalPassMap[passKey] || !!functionalPassMap[standardId];
}

export function isUnitCheckSatisfied(
  s: ProofStandard,
  roomName: string,
  proofMap: Record<string, string[]>,
  functionalPassMap: Record<string, boolean> | Set<string>,
): boolean {
  const urls = proofUrlsForUnitCheck(proofMap, s.id, roomName);
  const passKey = unitCheckProofKey(s.id, roomName);
  if (s.type === "functional_check") {
    return urls.length > 0 || functionalPassForKey(passKey, s.id, functionalPassMap);
  }
  return urls.length > 0;
}

/** True when every assigned room for this standard has proof or functional pass. */
export function isProofStandardFullySatisfied(
  s: ProofStandard,
  checklistRoomNames: string[],
  proofMap: Record<string, string[]>,
  functionalPassMap: Record<string, boolean> | Set<string>,
  generalLabel = "General",
): boolean {
  const rooms = getProofStandardRooms(s, checklistRoomNames, generalLabel);
  return rooms.every((room) => isUnitCheckSatisfied(s, room, proofMap, functionalPassMap));
}

/** Collapse per-room proof keys into one URL list per standard for submission storage. */
export function mergeProofMediaByStandardForSubmission(
  map: Record<string, string[]>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [key, urls] of Object.entries(map)) {
    if (!urls?.length) continue;
    const { standardId } = parseUnitCheckProofKey(key);
    if (!out[standardId]) out[standardId] = [];
    for (const u of urls) {
      if (!out[standardId].includes(u)) out[standardId].push(u);
    }
  }
  return out;
}
